import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  IconButton,
  Slide,
  SlideProps,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import type { Toast } from './contexts/ToastContext';

interface ToastProps {
  toast: Toast;
  onClose: (id: string) => void;
}

const toastIcons = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
};

const toastSeverity = {
  success: 'success' as const,
  error: 'error' as const,
  warning: 'warning' as const,
  info: 'info' as const,
};

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

export const ToastComponent: React.FC<ToastProps> = ({ toast, onClose }) => {
  const theme = useTheme();
  const IconComponent = toastIcons[toast.type];

  const handleClose = () => {
    onClose(toast.id);
  };

  const handleActionClick = () => {
    if (toast.action?.onClick) {
      toast.action.onClick();
    }
    handleClose();
  };

  return (
    <Snackbar
      open={true}
      autoHideDuration={toast.duration}
      onClose={handleClose}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        '& .MuiSnackbarContent-root': {
          backgroundColor: 'transparent',
          padding: 0,
          minWidth: 'unset',
          boxShadow: 'none',
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          boxShadow: theme.shadows[8],
          minWidth: 300,
          maxWidth: 400,
          overflow: 'hidden',
        }}
      >
        <Alert
          severity={toastSeverity[toast.type]}
          icon={<IconComponent />}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClose}
              sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            backgroundColor: 'transparent',
            color: 'inherit',
            padding: 2,
            '& .MuiAlert-icon': {
              color: theme.palette[toastSeverity[toast.type]].main,
            },
          }}
        >
          <Box sx={{ width: '100%' }}>
            <AlertTitle sx={{ fontWeight: 600, marginBottom: 0.5 }}>{toast.title}</AlertTitle>
            {toast.message && (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {toast.message}
              </Typography>
            )}
            {toast.action && (
              <Box sx={{ marginTop: 1 }}>
                <IconButton
                  size="small"
                  onClick={handleActionClick}
                  sx={{
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '20',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {toast.action.label}
                  </Typography>
                </IconButton>
              </Box>
            )}
          </Box>
        </Alert>
      </Box>
    </Snackbar>
  );
};
